import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useMembership } from "@/hooks/use-membership";
import { useCreateGroup } from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC, type FormEvent } from "react";
import { MemberSelectionList, type Subject } from "@/components/settings/organization/groups/MemberSelectionList";

type CreateGroupDialogProps = {
    open: boolean;
    onClose: () => void;
    onSuccess?: (groupId: string) => void;
};

export const CreateGroupDialog: FC<CreateGroupDialogProps> = ({ open, onClose, onSuccess }) => {
    const { toast } = useToast();
    const { membership } = useMembership();
    const createGroup = useCreateGroup();
    const [groupName, setGroupName] = useState("");
    const [groupDescription, setGroupDescription] = useState("");
    const [selectedSubjects, setSelectedSubjects] = useState<Map<string, Subject>>(new Map());

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!groupName.trim() || !membership?.organizationId) {
                return;
            }

            try {
                const subjects = selectedSubjects.size > 0 ? Array.from(selectedSubjects.values()) : undefined;

                const newGroup = await createGroup.mutateAsync({
                    organizationId: membership.organizationId,
                    name: groupName.trim(),
                    description: groupDescription.trim() || undefined,
                    subjects,
                });

                toast({
                    title: "Group created",
                    description:
                        selectedSubjects.size > 0
                            ? `Group "${groupName}" has been created with ${selectedSubjects.size} member${selectedSubjects.size === 1 ? "" : "s"}.`
                            : `Group "${groupName}" has been created successfully.`,
                });

                // Reset form
                setGroupName("");
                setGroupDescription("");
                setSelectedSubjects(new Map());
                onClose();
                onSuccess?.(newGroup.id);
            } catch (error) {
                toast({
                    title: "Failed to create group",
                    description: formatError(error),
                });
            }
        },
        [
            groupName,
            groupDescription,
            selectedSubjects,
            membership?.organizationId,
            createGroup,
            toast,
            onClose,
            onSuccess,
        ],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                // Reset form when dialog closes
                setGroupName("");
                setGroupDescription("");
                setSelectedSubjects(new Map());
                onClose();
            }
        },
        [onClose],
    );

    const isFormValid = groupName.trim().length >= 3 && groupName.trim().length <= 80;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="create-group-dialog"
                data-track-location={TrackLocations.CreateProjectModal}
                className="flex max-h-[90vh] h-[40rem] max-w-xl flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>Create new group</DialogTitle>
                </DialogHeader>

                <DialogBody className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
                    <form id="create-group-form" onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
                        <div className="flex min-h-0 flex-1 flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="group-name" className="font-medium">
                                    Group name
                                </Label>
                                <Input
                                    id="group-name"
                                    data-testid="group-name-input"
                                    name="groupName"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    placeholder="Product managers"
                                    autoFocus
                                    required
                                    minLength={3}
                                    maxLength={80}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="group-description" className="font-medium">
                                    Description
                                </Label>
                                <Input
                                    id="group-description"
                                    data-testid="group-description-input"
                                    name="groupDescription"
                                    value={groupDescription}
                                    onChange={(e) => setGroupDescription(e.target.value)}
                                    placeholder="optional"
                                    maxLength={255}
                                />
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col gap-2">
                                <Label className="font-medium">Add people</Label>
                                <MemberSelectionList
                                    selectedSubjects={selectedSubjects}
                                    onChangeSelection={setSelectedSubjects}
                                    showSearch={true}
                                    showSelectedCount={false}
                                    className="flex min-h-0 flex-1 flex-col space-y-2"
                                />
                            </div>
                        </div>
                    </form>
                </DialogBody>

                <DialogFooter className="flex items-center justify-between">
                    <p className="flex-1 text-sm text-content-secondary">
                        {selectedSubjects.size > 0 && `${selectedSubjects.size} members selected`}
                    </p>
                    <div className="flex gap-2">
                        <DialogClose asChild>
                            <Button variant="outline" data-testid="create-group-dialog-cancel">
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            form="create-group-form"
                            variant="primary"
                            disabled={!isFormValid || createGroup.isPending}
                            loading={createGroup.isPending}
                            data-testid="create-group-dialog-create"
                            data-tracking-id="create-group-dialog-create"
                        >
                            Create
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
